import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Package,
  FlaskConical,
  Clock,
  Loader2,
  CheckCircle,
  Eye,
  X,
  Apple,
  Sparkles,
  MessageSquare,
  User,
  Calendar,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { getSupabaseAdminClient } from '@parentpath/supabase-client';

const supabase = getSupabaseAdminClient();

type SuggestionStatus = 'open' | 'in_progress' | 'closed';
type TabType = 'products' | 'ingredients';

interface ProductSuggestion {
  id: string;
  user_id: string;
  brand: string;
  product_name: string;
  product_type: 'food' | 'cosmetic';
  barcode: string | null;
  similar_found: boolean;
  notes: string | null;
  status: SuggestionStatus;
  admin_notes: string | null;
  processed_by: string | null;
  processed_at: string | null;
  created_product_id: string | null;
  created_at: string;
  user_name?: string;
  user_email?: string;
  processed_by_name?: string;
}

interface IngredientReport {
  id: string;
  user_id: string;
  ingredient_name: string;
  product_type: 'food' | 'cosmetic';
  found_in_product: string | null;
  notes: string | null;
  status: SuggestionStatus;
  admin_notes: string | null;
  processed_by: string | null;
  processed_at: string | null;
  created_ingredient_id: string | null;
  created_at: string;
  user_name?: string;
  user_email?: string;
  processed_by_name?: string;
  linked_ingredient_name?: string;
}

const STATUS_CONFIG: Record<SuggestionStatus, { label: string; bg: string; text: string; icon: typeof Clock }> = {
  open: { label: 'Offen', bg: 'bg-blue-100', text: 'text-blue-700', icon: Clock },
  in_progress: { label: 'In Bearbeitung', bg: 'bg-amber-100', text: 'text-amber-700', icon: Loader2 },
  closed: { label: 'Erledigt', bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
};

function StatusBadge({ status }: { status: SuggestionStatus }) {
  const config = STATUS_CONFIG[status];
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${config.bg} ${config.text}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
}

function ProductTypeBadge({ type }: { type: 'food' | 'cosmetic' }) {
  if (type === 'cosmetic') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-pink-600 bg-pink-50 px-2 py-1 rounded-full">
        <Sparkles className="w-3 h-3" />
        Kosmetik
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">
      <Apple className="w-3 h-3" />
      Lebensmittel
    </span>
  );
}

// Detail Modal for Product Suggestions
function ProductSuggestionDetailModal({
  suggestion,
  onClose,
  onStatusChange,
  isUpdating,
}: {
  suggestion: ProductSuggestion;
  onClose: () => void;
  onStatusChange: (status: SuggestionStatus, notes?: string) => void;
  isUpdating: boolean;
}) {
  const [adminNotes, setAdminNotes] = useState(suggestion.admin_notes || '');
  const [newStatus, setNewStatus] = useState<SuggestionStatus>(suggestion.status);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">Produktvorschlag Details</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Product Info */}
          <div className="bg-slate-50 rounded-lg p-4 space-y-3">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-slate-900 text-lg">{suggestion.product_name}</h3>
                <p className="text-slate-600">{suggestion.brand}</p>
              </div>
              <ProductTypeBadge type={suggestion.product_type} />
            </div>
            {suggestion.barcode && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-slate-500">Barcode:</span>
                <code className="bg-white px-2 py-1 rounded font-mono text-sm">{suggestion.barcode}</code>
              </div>
            )}
            <div className="flex items-center gap-4 text-sm">
              <span className={suggestion.similar_found ? 'text-amber-600' : 'text-slate-500'}>
                {suggestion.similar_found ? '⚠️ Ähnliche Produkte gefunden' : '✓ Keine ähnlichen Produkte'}
              </span>
            </div>
          </div>

          {/* User Notes */}
          {suggestion.notes && (
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Notizen vom Nutzer
              </h4>
              <p className="text-slate-600 text-sm bg-slate-50 rounded-lg p-3">{suggestion.notes}</p>
            </div>
          )}

          {/* User Info */}
          <div className="flex items-center gap-4 text-sm text-slate-500">
            <div className="flex items-center gap-1">
              <User className="w-4 h-4" />
              {suggestion.user_name || suggestion.user_email || 'Unbekannt'}
            </div>
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {new Date(suggestion.created_at).toLocaleDateString('de-DE', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </div>
          </div>

          {/* Status Change */}
          <div>
            <h4 className="text-sm font-medium text-slate-700 mb-2">Status ändern</h4>
            <div className="flex gap-2">
              {(['open', 'in_progress', 'closed'] as SuggestionStatus[]).map((status) => {
                const config = STATUS_CONFIG[status];
                return (
                  <button
                    key={status}
                    onClick={() => setNewStatus(status)}
                    className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${
                      newStatus === status
                        ? `${config.bg} ${config.text} border-current`
                        : 'border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    {config.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Admin Notes */}
          <div>
            <h4 className="text-sm font-medium text-slate-700 mb-2">Admin-Notizen</h4>
            <textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              rows={3}
              placeholder="Interne Notizen zur Bearbeitung..."
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              onClick={onClose}
              className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              Abbrechen
            </button>
            {suggestion.created_product_id ? (
              <Link
                to={`/features/product-check/products?q=${encodeURIComponent(suggestion.product_name)}`}
                className="flex items-center gap-2 px-4 py-2 bg-teal-50 text-teal-700 rounded-lg hover:bg-teal-100 transition-colors"
              >
                <CheckCircle className="w-4 h-4" />
                Produkt bereits angelegt
              </Link>
            ) : (
              <Link
                to={`/features/product-check/products?fromSuggestion=${suggestion.id}`}
                className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
              >
                <Package className="w-4 h-4" />
                Produkt anlegen
              </Link>
            )}
            <button
              onClick={() => onStatusChange(newStatus, adminNotes)}
              disabled={isUpdating}
              className="flex items-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50 transition-colors"
            >
              {isUpdating ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              Speichern
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Detail Modal for Ingredient Reports
function IngredientReportDetailModal({
  report,
  onClose,
  onStatusChange,
  isUpdating,
}: {
  report: IngredientReport;
  onClose: () => void;
  onStatusChange: (status: SuggestionStatus, notes?: string) => void;
  isUpdating: boolean;
}) {
  const [adminNotes, setAdminNotes] = useState(report.admin_notes || '');
  const [newStatus, setNewStatus] = useState<SuggestionStatus>(report.status);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">Inhaltsstoff-Meldung Details</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Ingredient Info */}
          <div className="bg-slate-50 rounded-lg p-4 space-y-3">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-slate-900 text-lg">{report.ingredient_name}</h3>
                {report.found_in_product && (
                  <p className="text-slate-600 text-sm">Gefunden in: {report.found_in_product}</p>
                )}
              </div>
              <ProductTypeBadge type={report.product_type} />
            </div>
            {report.linked_ingredient_name && (
              <div className="flex items-center gap-2 text-sm text-green-600">
                <CheckCircle className="w-4 h-4" />
                Verknüpft mit: {report.linked_ingredient_name}
              </div>
            )}
          </div>

          {/* User Notes */}
          {report.notes && (
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Notizen vom Nutzer
              </h4>
              <p className="text-slate-600 text-sm bg-slate-50 rounded-lg p-3">{report.notes}</p>
            </div>
          )}

          {/* User Info */}
          <div className="flex items-center gap-4 text-sm text-slate-500">
            <div className="flex items-center gap-1">
              <User className="w-4 h-4" />
              {report.user_name || report.user_email || 'Unbekannt'}
            </div>
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {new Date(report.created_at).toLocaleDateString('de-DE', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </div>
          </div>

          {/* Status Change */}
          <div>
            <h4 className="text-sm font-medium text-slate-700 mb-2">Status ändern</h4>
            <div className="flex gap-2">
              {(['open', 'in_progress', 'closed'] as SuggestionStatus[]).map((status) => {
                const config = STATUS_CONFIG[status];
                return (
                  <button
                    key={status}
                    onClick={() => setNewStatus(status)}
                    className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${
                      newStatus === status
                        ? `${config.bg} ${config.text} border-current`
                        : 'border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    {config.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Admin Notes */}
          <div>
            <h4 className="text-sm font-medium text-slate-700 mb-2">Admin-Notizen</h4>
            <textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              rows={3}
              placeholder="Interne Notizen zur Bearbeitung..."
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              onClick={onClose}
              className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              Abbrechen
            </button>
            <Link
              to="/features/product-check"
              className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
            >
              <FlaskConical className="w-4 h-4" />
              Inhaltsstoff anlegen
            </Link>
            <button
              onClick={() => onStatusChange(newStatus, adminNotes)}
              disabled={isUpdating}
              className="flex items-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50 transition-colors"
            >
              {isUpdating ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              Speichern
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ProductSuggestionsPage() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabType>('products');
  const [statusFilter, setStatusFilter] = useState<SuggestionStatus | 'all'>('all');
  const [selectedProduct, setSelectedProduct] = useState<ProductSuggestion | null>(null);
  const [selectedIngredient, setSelectedIngredient] = useState<IngredientReport | null>(null);

  // Fetch product suggestions
  const { data: productSuggestions, isLoading: loadingProducts } = useQuery({
    queryKey: ['product-suggestions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('admin_product_suggestions')
        .select('*');
      if (error) throw error;
      return data as ProductSuggestion[];
    },
  });

  // Fetch ingredient reports
  const { data: ingredientReports, isLoading: loadingIngredients } = useQuery({
    queryKey: ['ingredient-reports'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('admin_ingredient_reports')
        .select('*');
      if (error) throw error;
      return data as IngredientReport[];
    },
  });

  // Update product suggestion
  const updateProductSuggestion = useMutation({
    mutationFn: async ({ id, status, adminNotes }: { id: string; status: SuggestionStatus; adminNotes?: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await (supabase
        .from('product_suggestions') as any)
        .update({
          status,
          admin_notes: adminNotes || null,
          processed_by: user?.id,
          processed_at: new Date().toISOString(),
        })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-suggestions'] });
      setSelectedProduct(null);
      toast.success('Status aktualisiert');
    },
    onError: (error: Error) => {
      toast.error(`Fehler: ${error.message}`);
    },
  });

  // Update ingredient report
  const updateIngredientReport = useMutation({
    mutationFn: async ({ id, status, adminNotes }: { id: string; status: SuggestionStatus; adminNotes?: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await (supabase
        .from('ingredient_reports') as any)
        .update({
          status,
          admin_notes: adminNotes || null,
          processed_by: user?.id,
          processed_at: new Date().toISOString(),
        })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ingredient-reports'] });
      setSelectedIngredient(null);
      toast.success('Status aktualisiert');
    },
    onError: (error: Error) => {
      toast.error(`Fehler: ${error.message}`);
    },
  });

  // Filter data
  const filteredProducts = productSuggestions?.filter((p) =>
    statusFilter === 'all' || p.status === statusFilter
  );
  const filteredIngredients = ingredientReports?.filter((i) =>
    statusFilter === 'all' || i.status === statusFilter
  );

  // Stats
  const openProductCount = productSuggestions?.filter((p) => p.status === 'open').length || 0;
  const openIngredientCount = ingredientReports?.filter((i) => i.status === 'open').length || 0;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center">
            <MessageSquare className="w-7 h-7 text-purple-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Nutzer-Vorschläge</h1>
            <p className="text-slate-500">Produktvorschläge und Inhaltsstoff-Meldungen verwalten</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Package className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">{productSuggestions?.length || 0}</div>
              <div className="text-sm text-slate-500">Produktvorschläge</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">{openProductCount}</div>
              <div className="text-sm text-slate-500">Offen (Produkte)</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
              <FlaskConical className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">{ingredientReports?.length || 0}</div>
              <div className="text-sm text-slate-500">Inhaltsstoff-Meldungen</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">{openIngredientCount}</div>
              <div className="text-sm text-slate-500">Offen (Inhaltsstoffe)</div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200">
        {/* Tabs & Filters */}
        <div className="border-b border-slate-200 px-6 py-4">
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            {/* Tabs */}
            <div className="flex gap-1 p-1 bg-slate-100 rounded-lg w-fit">
              <button
                onClick={() => setActiveTab('products')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  activeTab === 'products'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Package className="w-4 h-4" />
                Produktvorschläge
                {openProductCount > 0 && (
                  <span className="bg-amber-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                    {openProductCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('ingredients')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  activeTab === 'ingredients'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <FlaskConical className="w-4 h-4" />
                Inhaltsstoff-Meldungen
                {openIngredientCount > 0 && (
                  <span className="bg-amber-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                    {openIngredientCount}
                  </span>
                )}
              </button>
            </div>

            {/* Status Filter */}
            <div className="flex gap-1 p-1 bg-slate-100 rounded-lg w-fit">
              {[
                { value: 'all', label: 'Alle' },
                { value: 'open', label: 'Offen' },
                { value: 'in_progress', label: 'In Bearbeitung' },
                { value: 'closed', label: 'Erledigt' },
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => setStatusFilter(option.value as SuggestionStatus | 'all')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                    statusFilter === option.value
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Table Content */}
        <div className="overflow-x-auto">
          {activeTab === 'products' ? (
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Produkt</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Typ</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Barcode</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Status</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Datum</th>
                  <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Aktionen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loadingProducts ? (
                  <tr>
                    <td colSpan={6} className="py-8 text-center">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto text-teal-500" />
                    </td>
                  </tr>
                ) : filteredProducts?.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-slate-500">
                      Keine Produktvorschläge vorhanden
                    </td>
                  </tr>
                ) : (
                  filteredProducts?.map((suggestion) => (
                    <tr key={suggestion.id} className="hover:bg-slate-50">
                      <td className="py-3 px-4">
                        <div>
                          <span className="font-medium text-slate-900">{suggestion.product_name}</span>
                          <span className="text-slate-500 text-sm ml-2">({suggestion.brand})</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <ProductTypeBadge type={suggestion.product_type} />
                      </td>
                      <td className="py-3 px-4">
                        {suggestion.barcode ? (
                          <code className="text-xs bg-slate-100 px-2 py-1 rounded">{suggestion.barcode}</code>
                        ) : (
                          <span className="text-slate-400 text-sm">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex flex-col items-start gap-1">
                          <StatusBadge status={suggestion.status} />
                          {suggestion.created_product_id && (
                            <Link
                              to={`/features/product-check/products?q=${encodeURIComponent(suggestion.product_name)}`}
                              className="inline-flex items-center gap-1 text-xs text-teal-600 hover:text-teal-700 hover:underline"
                            >
                              <Package className="w-3 h-3" />
                              Produkt angelegt
                            </Link>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm text-slate-500">
                          {new Date(suggestion.created_at).toLocaleDateString('de-DE')}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end">
                          <button
                            onClick={() => setSelectedProduct(suggestion)}
                            className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Inhaltsstoff</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Typ</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Gefunden in</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Status</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Datum</th>
                  <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Aktionen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loadingIngredients ? (
                  <tr>
                    <td colSpan={6} className="py-8 text-center">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto text-teal-500" />
                    </td>
                  </tr>
                ) : filteredIngredients?.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-slate-500">
                      Keine Inhaltsstoff-Meldungen vorhanden
                    </td>
                  </tr>
                ) : (
                  filteredIngredients?.map((report) => (
                    <tr key={report.id} className="hover:bg-slate-50">
                      <td className="py-3 px-4">
                        <span className="font-medium text-slate-900">{report.ingredient_name}</span>
                        {report.linked_ingredient_name && (
                          <span className="text-green-600 text-xs ml-2">✓ Verknüpft</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <ProductTypeBadge type={report.product_type} />
                      </td>
                      <td className="py-3 px-4">
                        {report.found_in_product ? (
                          <span className="text-sm text-slate-600">{report.found_in_product}</span>
                        ) : (
                          <span className="text-slate-400 text-sm">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <StatusBadge status={report.status} />
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm text-slate-500">
                          {new Date(report.created_at).toLocaleDateString('de-DE')}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end">
                          <button
                            onClick={() => setSelectedIngredient(report)}
                            className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Detail Modals */}
      {selectedProduct && (
        <ProductSuggestionDetailModal
          suggestion={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onStatusChange={(status, notes) =>
            updateProductSuggestion.mutate({ id: selectedProduct.id, status, adminNotes: notes })
          }
          isUpdating={updateProductSuggestion.isPending}
        />
      )}

      {selectedIngredient && (
        <IngredientReportDetailModal
          report={selectedIngredient}
          onClose={() => setSelectedIngredient(null)}
          onStatusChange={(status, notes) =>
            updateIngredientReport.mutate({ id: selectedIngredient.id, status, adminNotes: notes })
          }
          isUpdating={updateIngredientReport.isPending}
        />
      )}
    </div>
  );
}
